/**
 * The session.
 *
 * One store owns who is signed in, what they may do, and which branch and
 * section they are working in. Components read from here; nothing else knows
 * about tokens.
 */

import { create } from "zustand";

import { ApiError, configureAuth, NetworkError } from "@/api/client";
import { authApi } from "@/api/endpoints/auth";
import { authRepository, type Session } from "@/repositories/authRepository";
import { offlineGrantRepository, type OfflineCheck } from "@/repositories/offlineGrantRepository";

type Status = "loading" | "authenticated" | "anonymous";

interface AuthState {
  status: Status;
  session: Session | null;
  error: string | null;

  /** Rehydrate from storage on app start. */
  restore: () => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  loginWithPin: (staffCode: string, pin: string, deviceToken: string) => Promise<void>;
  /**
   * Adopt a session the cellphone sign-in produced. The PIN steps happen in the
   * screen, because a new number takes a detour through registration that the
   * store has no part in.
   */
  signInWith: (session: Session) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Turn a session opened offline into a real one, now the server answers.
   * Does nothing for an ordinary session, or while the server is unreachable.
   */
  reconnect: () => Promise<void>;

  setActiveScope: (branchId: string | null, sectionId: string | null) => Promise<void>;

  /**
   * Work in a different business.
   *
   * Null means their own. The server validates the choice against their
   * memberships, so this is a preference rather than a grant — asking for one
   * they hold no membership in lands them back in their own.
   */
  setActiveMerchant: (merchantId: string | null) => Promise<void>;
  /**
   * Work in a shop as SmartSpaza support, read-only. The server has already
   * opened the session; this points requests at the shop and reloads the
   * session so the interface shows that shop and its view-only permissions.
   */
  enterSupportView: (merchantId: string) => Promise<void>;
  /** Leave it: back to the platform, with the shop's cached data forgotten. */
  exitSupportView: () => Promise<void>;

  /**
   * Adopt a token pair minted outside the refresh cycle.
   *
   * Changing one's password blacklists every outstanding refresh token, the
   * caller's own included — the server cannot tell this browser from the tablet
   * on the counter. It returns a fresh pair and this swaps it in, so the person
   * who just succeeded is not signed out by their own success.
   */
  applyTokens: (access: string, refresh: string) => Promise<void>;

  /** Reflect an edited profile in the session the interface renders from. */
  applyProfile: (profile: { displayName: string; email: string | null }) => Promise<void>;

  /** Does the signed-in user hold this permission? */
  can: (codename: string) => boolean;
  canAny: (...codenames: string[]) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "loading",
  session: null,
  error: null,

  restore: async () => {
    const stored = await authRepository.load();

    if (!stored) {
      set({ status: "anonymous", session: null });
      return;
    }

    // A session opened against an offline grant does not survive a restart.
    // It has no server tokens, and the PIN that would get them was only ever
    // held in memory — so after a power cut the cashier types it again, which
    // works with or without signal.
    if (stored.offline) {
      await authRepository.clear();
      set({ status: "anonymous", session: null });
      return;
    }

    // Trust the stored session immediately so the interface renders without a
    // flash of the login screen, then confirm it against the server. On a
    // terminal that is offline the confirmation simply fails and the cached
    // session stands — which is the entire point of persisting it.
    set({ status: "authenticated", session: stored });

    try {
      const fresh = await authApi.me({
        access: stored.accessToken,
        refresh: stored.refreshToken,
      });
      // The tokens are whatever the store holds *now*, not the ones read from
      // storage above. If the stored access token had expired, the call just
      // made refreshed it — and rotation blacklisted the stored refresh token —
      // so writing the stored pair back would sign the person out on their next
      // request.
      const current = get().session;
      const merged: Session = {
        ...fresh,
        accessToken: current?.accessToken ?? fresh.accessToken,
        refreshToken: current?.refreshToken ?? fresh.refreshToken,
        // Carried across a refresh: somebody who was working in a client's shop
        // and reloaded the page should still be in it, not thrown back to their
        // own without being told.
        activeMerchantId: stored.activeMerchantId,
        activeBranchId: stored.activeBranchId,
        activeSectionId: stored.activeSectionId,
      };
      await authRepository.save(merged);
      set({ session: merged });
    } catch {
      // Offline, or the token expired and could not be refreshed. Either way the
      // cached session remains usable; a genuinely rejected token clears the
      // session through `onUnauthorized` below.
    }
  },

  loginWithPassword: async (email, password) => {
    set({ error: null });
    try {
      const session = await authApi.loginWithPassword(email, password);
      await authRepository.save(session);
      set({ status: "authenticated", session, error: null });
    } catch (error) {
      set({ error: messageFor(error) });
      throw error;
    }
  },

  signInWith: async (session) => {
    await authRepository.save(session);
    set({ status: "authenticated", session, error: null });
  },

  loginWithPin: async (staffCode, pin, deviceToken) => {
    set({ error: null });
    try {
      const session = await authApi.loginWithPin(staffCode, pin, deviceToken);
      await authRepository.save(session);
      set({ status: "authenticated", session, error: null });
      pendingReconnect = null;
      // In the background: the cashier is signed in already, and deriving the
      // verifier takes a moment. A grant that failed to save only means this
      // cashier needs signal for their next sign-in here.
      void offlineGrantRepository.remember(session, pin).catch(() => {});
    } catch (error) {
      // No signal. A paired till may still let this cashier in, against the
      // grant it was given the last time they signed in here online.
      if (error instanceof NetworkError && deviceToken) {
        const check = await offlineGrantRepository.check(staffCode, pin);
        if (check.ok) {
          await authRepository.save(check.session);
          set({ status: "authenticated", session: check.session, error: null });
          pendingReconnect = { staffCode, pin, deviceToken };
          return;
        }
        set({ error: offlineMessage(check) });
        throw error;
      }

      if (error instanceof ApiError && REFUSALS.has(error.code)) {
        void offlineGrantRepository.revokeIfStale(staffCode, pin).catch(() => {});
      }
      set({ error: messageFor(error) });
      throw error;
    }
  },

  reconnect: async () => {
    const session = get().session;
    if (!session?.offline) return;

    const pending = pendingReconnect;
    if (!pending) {
      // Nothing to reconnect with. Cannot normally happen — restore drops
      // offline sessions — but a session with no way to get tokens must not
      // sit there pretending.
      await get().logout();
      return;
    }

    try {
      const fresh = await authApi.loginWithPin(pending.staffCode, pending.pin, pending.deviceToken);
      // The cashier may have moved to another counter while offline.
      const merged: Session = {
        ...fresh,
        activeBranchId: session.activeBranchId,
        activeSectionId: session.activeSectionId,
      };
      await authRepository.save(merged);
      set({ session: merged });
      pendingReconnect = null;
      void offlineGrantRepository.remember(merged, pending.pin).catch(() => {});
    } catch (error) {
      if (error instanceof ApiError && REFUSALS.has(error.code)) {
        // The server no longer accepts this cashier's PIN: changed, locked out
        // by an administrator, or the account stopped. Their offline sales are
        // already queued under their name and will still upload; the session
        // and the grant end here.
        await offlineGrantRepository.forget(session.user.id).catch(() => {});
        pendingReconnect = null;
        await get().logout();
      }
      // Anything else — still no signal, a server error — try again next time.
    }
  },

  logout: async () => {
    const session = get().session;

    pendingReconnect = null;
    if (session && !session.offline) {
      try {
        await authApi.logout(session.refreshToken);
      } catch {
        // The server may be unreachable. Sign out locally regardless — refusing
        // to would leave a cashier's session open on a shared terminal.
      }
    }

    // SmartSpaza staff never need a shop's cached data once they have gone —
    // leaving from inside a support view must not leave the shop's sales and
    // customers in this browser. A till is untouched: its outbox may hold
    // unsynced sales that belong to the terminal, not to whoever signed in.
    if (session?.user.isPlatformStaff) {
      try {
        await authRepository.forgetShopData();
      } catch {
        // Signing out must not depend on the cache being clearable.
      }
    }

    await authRepository.clear();
    set({ status: "anonymous", session: null, error: null });
  },

  setActiveScope: async (branchId, sectionId) => {
    const updated = await authRepository.patch({
      activeBranchId: branchId,
      activeSectionId: sectionId,
    });
    if (updated) set({ session: updated });
  },

  setActiveMerchant: async (merchantId) => {
    // The branch and section are cleared, and that is not tidiness: they name
    // rows in the business being left, and carrying them into another would send
    // headers pointing at a branch that merchant does not have. The server
    // ignores an out-of-scope branch, so nothing would break — it would quietly
    // stay narrowed to something nobody can see.
    const updated = await authRepository.patch({
      activeMerchantId: merchantId,
      activeBranchId: null,
      activeSectionId: null,
    });
    if (updated) set({ session: updated });
  },

  enterSupportView: async (merchantId) => {
    await get().setActiveMerchant(merchantId);
    await reloadSession();
  },

  exitSupportView: async () => {
    await get().setActiveMerchant(null);
    try {
      await authRepository.forgetShopData();
    } catch {
      // A browser that cannot clear its cache must still be able to leave.
    }
    await reloadSession();
  },

  applyTokens: async (access, refresh) => {
    // Set state before persisting, for the reason spelled out in
    // `onTokensRefreshed`: anything retrying a request reads the token straight
    // back out of this store and must not be handed the spent one.
    const current = get().session;
    if (!current) return;

    set({ session: { ...current, accessToken: access, refreshToken: refresh } });
    await authRepository.patch({ accessToken: access, refreshToken: refresh });
  },

  applyProfile: async ({ displayName, email }) => {
    const current = get().session;
    if (!current) return;

    const next: Session = { ...current, user: { ...current.user, displayName, email } };
    set({ session: next });
    await authRepository.save(next);
  },

  can: (codename) => {
    const session = get().session;
    if (!session) return false;
    // Platform staff hold every permission implicitly, exactly as the backend
    // resolves it — except inside a shop on a support view, where they hold the
    // view-only set the server sent, so nothing offers a change it would refuse.
    if (session.user.isPlatformStaff && !session.supportView) return true;
    return session.permissions.includes(codename);
  },

  canAny: (...codenames) => codenames.some((code) => get().can(code)),
}));

/**
 * Ask the server who we are now, keeping the client's own choices.
 *
 * After changing the active shop the cached session still describes the old
 * one — its name, its permissions, whether it is a support view. This replaces
 * that with the server's answer for the shop now being sent.
 */
async function reloadSession(): Promise<void> {
  const current = useAuthStore.getState().session;
  if (!current) return;

  const fresh = await authApi.me({ access: current.accessToken, refresh: current.refreshToken });
  const latest = useAuthStore.getState().session ?? current;
  const merged: Session = {
    ...fresh,
    accessToken: latest.accessToken,
    refreshToken: latest.refreshToken,
    activeMerchantId: latest.activeMerchantId,
    activeBranchId: latest.activeBranchId,
    activeSectionId: latest.activeSectionId,
  };
  await authRepository.save(merged);
  useAuthStore.setState({ session: merged });
}

/**
 * What an offline session needs to become an online one: the PIN, held in
 * memory only, for exactly as long as the offline session lasts.
 */
let pendingReconnect: { staffCode: string; pin: string; deviceToken: string } | null = null;

/** The server saying no to this person, as opposed to not answering. */
const REFUSALS = new Set(["INVALID_CREDENTIALS", "ACCOUNT_INACTIVE"]);

function offlineMessage(check: Exclude<OfflineCheck, { ok: true }>): string {
  switch (check.reason) {
    case "NO_GRANT":
      return "No signal, and you have not signed in on this till with signal yet. Sign in once when there is signal, and after that it works without.";
    case "EXPIRED":
      return "No signal, and it has been too long since you last signed in on this till with signal. Sign in once when there is signal.";
    case "LOCKED": {
      const time = new Date(check.until).toLocaleTimeString("en-ZA", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `Too many wrong PINs. Try again after ${time}.`;
    }
    case "WRONG_PIN":
      return "That staff code and PIN do not match.";
  }
}

function messageFor(error: unknown): string {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail?: string }).detail;
    if (detail) return detail;
  }
  if (error instanceof Error && error.name === "NetworkError") {
    return "Could not reach the server. Check the connection and try again.";
  }
  return "Sign-in failed. Please try again.";
}

/**
 * Wire the API client to this store.
 *
 * Called once at start-up. Keeping it here rather than in the client avoids a
 * circular import — the client must not know about the store that uses it.
 */
export function connectAuthToApiClient(): void {
  configureAuth({
    getAccessToken: () => useAuthStore.getState().session?.accessToken ?? null,

    getRefreshToken: () => useAuthStore.getState().session?.refreshToken ?? null,

    onTokensRefreshed: (access, refresh) => {
      // **Synchronously**, before anything else runs. The client calls this and
      // immediately retries the request that got the 401, reading the token
      // straight back out of this store — so an update that only landed after an
      // IndexedDB write resolved handed the retry the *old* access token. That
      // retry 401s too, refresh is not attempted twice, and the cashier is
      // signed out.
      //
      // Worse with rotation on: the refresh token that was just spent is
      // blacklisted server-side, so the stale copy left in the store could never
      // succeed either. The result was a till that signed its cashier out every
      // time the access token expired — mid-shift, mid-sale.
      const current = useAuthStore.getState().session;
      if (current) {
        useAuthStore.setState({
          session: { ...current, accessToken: access, refreshToken: refresh },
        });
      }
      // Persisting can take its time; nothing reads the database for this.
      void authRepository.patch({ accessToken: access, refreshToken: refresh });
    },

    onUnauthorized: () => {
      // Reached only when a refresh has already been attempted and rejected, so
      // the credential is genuinely dead rather than merely expired.
      //
      // Except for a session opened offline, which never had a credential: the
      // signal is back before `reconnect` has fetched one. Signing the cashier
      // out for that would undo offline sign-in the moment it stopped mattering.
      const state = useAuthStore.getState();
      if (state.session?.offline) {
        void state.reconnect();
        return;
      }
      void state.logout();
    },

    // Scope-narrowing headers. The server validates these against what the user
    // is actually entitled to and ignores anything out of scope — they can
    // narrow, never widen.
    getScopeHeaders: () => {
      const session = useAuthStore.getState().session;
      if (!session) return {};

      const headers: Record<string, string> = {};
      // Selects a merchant rather than narrowing one, so the server checks it
      // against the caller's memberships instead of merely intersecting it.
      if (session.activeMerchantId) headers["X-SmartSpaza-Merchant"] = session.activeMerchantId;
      if (session.activeBranchId) headers["X-SmartSpaza-Branch"] = session.activeBranchId;
      if (session.activeSectionId) headers["X-SmartSpaza-Section"] = session.activeSectionId;
      if (session.device?.id) headers["X-SmartSpaza-Device"] = session.device.id;
      return headers;
    },
  });
}
